import { useEffect, useState } from 'react';
import { Table, Card, Input, Switch, Typography, Flex } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useAuth } from 'react-oidc-context';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import AntDButton from 'src/components/Button';
import { ScrollableContent } from 'src/components/ScrollableContent';
import {
  getEmailNotificationSettings,
  saveEmailNotificationSettings,
} from 'src/redux/features/users/usersSlice';
import en from 'src/messages/en.json';
import { setError } from 'src/redux/features/common/commonSlice';

const { Title } = Typography;

interface EmailNotificationUser {
  email: string;
  reason: string | null;
  notify: boolean | null;
}

const EditEmailNotifications = () => {
  const auth = useAuth();
  const dispatch = useDispatch();

  const [users, setUsers] = useState<EmailNotificationUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<EmailNotificationUser[]>(
    [],
  );
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  //  Fetch Users
  useEffect(() => {
    const fetchUsers = async () => {
      if (!auth?.user?.profile?.email) return;

      try {
        setIsLoading(true);

        // API CALL
        const response = await dispatch(
          getEmailNotificationSettings({
            managerEmail: auth.user.profile.email,
          }),
        ).unwrap();

        // If notify is null -> default true
        const formattedData = response.map((item: any) => ({
          email: item.email,
          reason: item.reason,
          notify: item.notify ?? true,
        }));

        setUsers(formattedData);
        setFilteredUsers(formattedData);
      } catch (error) {
        dispatch(setError(en.Common.Error.fetchData));
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [auth?.user?.profile?.email]);

  // Search filter
  useEffect(() => {
    if (!search) {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter((u) =>
        u.email.toLowerCase().includes(search.toLowerCase()),
      );

      setFilteredUsers(filtered);
    }
  }, [search, users]);

  // Handle Toggle
  const handleToggle = (checked: boolean, record: EmailNotificationUser) => {
    const updated = users.map((u) =>
      u.email === record.email
        ? { ...u, notify: checked, reason: checked ? null : u.reason }
        : u,
    );

    setUsers(updated);
  };

  //  Handle Reason Change
  const handleReasonChange = (value: string, record: EmailNotificationUser) => {
    const updated = users.map((u) =>
      u.email === record.email ? { ...u, reason: value } : u,
    );

    setUsers(updated);
  };

  // Save
  const handleSave = async () => {
    // Validate reason if notify = false
    const invalid = users.find((u) => u.notify === false && !u.reason?.trim());

    if (invalid) {
      dispatch(setError(en.Timesheet.Error.reasonMandatory));

      return;
    }

    try {
      setIsSaving(true);

      const payload = users.map((u) => ({
        email: u.email,
        reason: u.notify ? null : u.reason,
        notify: u.notify,
      }));

      await dispatch(saveEmailNotificationSettings(payload)).unwrap();

      toast.success(en.Timesheet.Success.emailNotificationsSaved);
      await dispatch(
        getEmailNotificationSettings({ managerEmail: auth.user.profile.email }),
      );
    } catch (error) {
      dispatch(setError(en.Common.Error.saveData));
    } finally {
      setIsSaving(false);
    }
  };

  // Table Columns
  const columns = [
    {
      title: en.Timesheet.Title.users,
      dataIndex: 'email',
      key: 'email',
      width: '35%',
    },
    {
      title: en.Timesheet.Title.sendNotifications,
      dataIndex: 'notify',
      key: 'notify',
      width: '20%',
      render: (_: any, record: EmailNotificationUser) => (
        <Switch
          checked={record.notify ?? true}
          onChange={(checked) => handleToggle(checked, record)}
          size="small"
        />
      ),
    },
    {
      title: (
        <div style={{ position: 'relative', display: 'inline-block' }}>
          {en.Timesheet.Title.reasonForOptingOut}
          <span
            style={{
              position: 'absolute',
              top: -4,
              right: -12,
              color: 'red',
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            *
          </span>
        </div>
      ),
      key: 'reason',
      width: '45%',
      render: (_: any, record: EmailNotificationUser) =>
        record.notify ? (
          <div style={{ height: 32, width: 50 }} />
        ) : (
          <Input
            placeholder={en.Timesheet.Placeholder.reason}
            value={record.reason ?? ''}
            maxLength={50}
            style={{ width: '70%' }}
            onChange={(e) => handleReasonChange(e.target.value, record)}
          />
        ),
    },
  ];

  return (
    <>
      <Title style={{ textAlign: 'left' }} level={4}>
        {en.Timesheet.Title.editEmailNotifications}
      </Title>

      <Flex gap="small" vertical>
        <Card size="small">
          <Flex justify="flex-end">
            <Input
              placeholder={en.Common.Placeholder.search}
              value={search}
              prefix={<SearchOutlined />}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 260 }}
              allowClear
            />
          </Flex>
        </Card>

        <Card size="small">
          <ScrollableContent
            minHeight="calc(100vh - 250px)"
            maxHeight="calc(100vh - 250px)"
          >
            <Table
              dataSource={filteredUsers}
              columns={columns}
              rowKey="email"
              loading={isLoading}
              pagination={false}
            />
          </ScrollableContent>

          <Flex
            style={{
              borderTop: '1px solid #e0e0e0',
              paddingTop: 12,
            }}
          >
            <Flex style={{ marginLeft: 'auto' }}>
              <AntDButton
                type="primary"
                onClick={handleSave}
                loading={isSaving}
                htmlType="button"
                statusOnComplete="Save"
                status="Saving"
              />
            </Flex>
          </Flex>
        </Card>
      </Flex>
    </>
  );
};

export default EditEmailNotifications;
